/* =====================================================================
 * AdminRecentActivityPanel — 최근 기록 (owner 전용, 최근기록 탭)
 *
 * 전체 사용자 횡단 요청 피드: 어떤 사용자가 어떤 기능에서 어떤 질의를 했는지
 * 최신순으로 본다. user_usage_ledger 를 message_id(요청)로 묶은 메타(사용자·
 * 기능·시각·상태·토큰)를 표로 보여주고, 질의/응답 본문은 행별 "본문 보기"로
 * 게이트 조회한다(기존 /activity/messages, 서버 감사 로그 기록). 기능·상태·
 * 기간·검색 필터. 기본 기간은 최근 14일(스캔 한정). PostgreSQL 전용(B2C).
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Loader2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import {
  getRecentActivity,
  getUserActivityMessages,
  type RecentActivityItem,
  type GatedMessageItem,
} from '@auw/api-client';
import { Input, SelectDropdown, type SelectOption } from '../common';

/** 한 페이지당 행 수. 목록 컨테이너가 뷰포트를 넘지 않도록 페이지로 끊는다. */
const PAGE_SIZE = 20;

// 활동 내역 필터 옵션 — i18n appManage.features.* / activity.status_* 와 정합.
const FEATURE_KEYS = [
  'agent', 'live-agent', 'doc-agent', 'cad-agent', 'cad-material',
  'estimate-agent', 'document', 'message', 'unknown',
] as const;
const STATUS_KEYS = ['completed', 'failed', 'cancelled', 'in_progress'] as const;

type Preset = 'all' | 'today' | 'week' | 'month' | 'custom';

const kstTodayISO = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
const addDaysISO = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

function formatTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export const AdminRecentActivityPanel: React.FC = () => {
  const { t } = useTranslation('admin');
  const addToast = useChatStore((s) => s.addToast);

  const [items, setItems] = useState<RecentActivityItem[]>([]);
  const [page, setPage] = useState(0); // 0-based
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // 필터: 기간(KST)·기능·상태·검색.
  const today = kstTodayISO();
  const [preset, setPreset] = useState<Preset>('all');
  const [from, setFrom] = useState(() => addDaysISO(today, -6));
  const [to, setTo] = useState(today);
  const [feature, setFeature] = useState('');
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState(''); // debounce 적용된 실제 검색어

  // 행별 본문 토글: 열린 요청의 message_id.
  const [openMsg, setOpenMsg] = useState<string | null>(null);
  const [messages, setMessages] = useState<Record<string, GatedMessageItem[]>>({});
  const [msgLoading, setMsgLoading] = useState<string | null>(null);

  const load = useCallback(async (nextPage: number) => {
    setLoading(true);
    try {
      const res = await getRecentActivity({
        limit: PAGE_SIZE,
        offset: nextPage * PAGE_SIZE,
        dateFrom: preset === 'all' ? undefined : from,
        dateTo: preset === 'all' ? undefined : to,
        feature: feature || undefined,
        status: status || undefined,
        q: q || undefined,
      });
      setItems(res.items); // 페이지 교체(누적 아님) — 컨테이너 높이를 가둔다.
      setPage(nextPage);
      setHasMore(res.has_more);
      setOpenMsg(null); // 페이지가 바뀌면 펼친 본문은 접는다.
    } catch (e) {
      addToast({ type: 'error', title: t('appManage.recent.loadFail'), description: e instanceof Error ? e.message : undefined });
    } finally {
      setLoading(false);
    }
  }, [preset, from, to, feature, status, q, addToast, t]);

  // 필터 변경 시 첫 페이지부터 다시 로드.
  useEffect(() => { void load(0); }, [load]);

  // 검색 debounce(300ms).
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(value.trim()), 300);
  }, []);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'today') { setFrom(today); setTo(today); }
    else if (p === 'week') { setFrom(addDaysISO(today, -6)); setTo(today); }
    else if (p === 'month') { setFrom(addDaysISO(today, -29)); setTo(today); }
  };

  const featureOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('appManage.activity.filterAllFeatures') },
      ...FEATURE_KEYS.map((f) => ({ value: f, label: t(`appManage.features.${f}`, f) })),
    ],
    [t],
  );
  const statusOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('appManage.activity.filterAllStatus') },
      ...STATUS_KEYS.map((s) => ({ value: s, label: t(`appManage.activity.status_${s}`, s) })),
    ],
    [t],
  );

  const toggle = useCallback(async (row: RecentActivityItem) => {
    const msgKey = row.message_id ?? '';
    const itemId = row.item_id ?? '';
    if (!msgKey || !itemId) return;
    if (openMsg === msgKey) { setOpenMsg(null); return; }
    setOpenMsg(msgKey);
    if (!messages[msgKey]) {
      setMsgLoading(msgKey);
      try {
        const res = await getUserActivityMessages(row.user_id, itemId, { messageId: msgKey, limit: 200 });
        setMessages((prev) => ({ ...prev, [msgKey]: res.messages }));
      } catch (e) {
        addToast({ type: 'error', title: t('appManage.activity.contentFail'), description: e instanceof Error ? e.message : undefined });
        setOpenMsg(null);
      } finally {
        setMsgLoading(null);
      }
    }
  }, [openMsg, messages, addToast, t]);

  return (
    <>
      <PrivacyNote>{t('appManage.activity.privacyNote')}</PrivacyNote>

      <Filters>
        <SearchWrap>
          <Input
            variant="search"
            placeholder={t('appManage.recent.searchPlaceholder')}
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
          />
        </SearchWrap>
        <Presets>
          {(['all', 'today', 'week', 'month'] as Preset[]).map((p) => (
            <PresetBtn key={p} type="button" $active={preset === p} onClick={() => applyPreset(p)}>
              {p === 'all' ? t('appManage.overview.preset_all') : t(`appManage.overview.preset_${p}`)}
            </PresetBtn>
          ))}
        </Presets>
        {preset !== 'all' && (
          <Range>
            <DateInput type="date" value={from} max={to} onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }} />
            <span>~</span>
            <DateInput type="date" value={to} max={today} min={from} onChange={(e) => { setPreset('custom'); setTo(e.target.value); }} />
          </Range>
        )}
        <FilterSelect options={featureOptions} value={feature} onChange={setFeature} size="sm" placeholder={t('appManage.activity.filterAllFeatures')} />
        <FilterSelect options={statusOptions} value={status} onChange={setStatus} size="sm" placeholder={t('appManage.activity.filterAllStatus')} />
      </Filters>

      {loading && items.length === 0 ? (
        <Center><Loader2 size={18} className="spin" /></Center>
      ) : items.length === 0 ? (
        <Center>{t('appManage.recent.empty')}</Center>
      ) : (
        <>
          <ListScroll>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>{t('appManage.recent.userColumn')}</Th>
                  <Th>{t('appManage.activity.feature')}</Th>
                  <Th>{t('appManage.activity.time')}</Th>
                  <Th>{t('appManage.activity.status')}</Th>
                  <Th $right>{t('appManage.activity.tokens')}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const msgKey = it.message_id ?? '';
                  const itemId = it.item_id ?? '';
                  const isOpen = !!msgKey && openMsg === msgKey;
                  return (
                    <React.Fragment key={`${msgKey || idx}-${it.user_id}`}>
                      <tr>
                        <Td>
                          <UserCell>
                            <UserName>{it.name || '—'}</UserName>
                            <UserEmail>{it.email || it.user_id}</UserEmail>
                          </UserCell>
                        </Td>
                        <Td><FeatureChip>{t(`appManage.features.${it.feature}`, it.feature)}</FeatureChip></Td>
                        <Td><TimeText><Clock size={12} /> {formatTime(it.created_at)}</TimeText></Td>
                        <Td><StatusBadge $status={it.status}>{t(`appManage.activity.status_${it.status}`, it.status)}</StatusBadge></Td>
                        <Td $right><Tokens>{it.tokens.toLocaleString()}</Tokens></Td>
                        <Td $right>
                          {itemId && (
                            <LinkBtn type="button" onClick={() => toggle(it)}>
                              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              {isOpen ? t('appManage.activity.hideContent') : t('appManage.activity.viewContent')}
                            </LinkBtn>
                          )}
                        </Td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <DetailCell colSpan={6}>
                            {msgLoading === msgKey ? (
                              <Center><Loader2 size={16} className="spin" /></Center>
                            ) : (
                              <Conversation>
                                {(messages[msgKey] ?? []).map((m, i) => (
                                  <Msg key={m.message_id ?? i} $role={m.role}>
                                    <MsgRole>{t(`appManage.activity.role_${m.role}`, m.role)}</MsgRole>
                                    <MsgBubble $role={m.role}>{m.content}</MsgBubble>
                                  </Msg>
                                ))}
                                {(messages[msgKey] ?? []).length === 0 && <Center>{t('appManage.activity.empty')}</Center>}
                              </Conversation>
                            )}
                          </DetailCell>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
          </ListScroll>
          {(page > 0 || hasMore) && (
            <Pagination>
              <PageNavBtn
                type="button"
                disabled={page === 0 || loading}
                onClick={() => load(page - 1)}
                aria-label={t('appManage.prevPage')}
              >
                <ChevronLeft size={16} />
              </PageNavBtn>
              <PageIndicator>
                {loading ? <Loader2 size={13} className="spin" /> : t('appManage.recent.page', { page: page + 1 })}
              </PageIndicator>
              <PageNavBtn
                type="button"
                disabled={!hasMore || loading}
                onClick={() => load(page + 1)}
                aria-label={t('appManage.nextPage')}
              >
                <ChevronRight size={16} />
              </PageNavBtn>
            </Pagination>
          )}
        </>
      )}
    </>
  );
};

export default AdminRecentActivityPanel;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Center = styled.div`
  display: flex; align-items: center; justify-content: center;
  padding: 40px 0; font-size: 0.8125rem; color: ${({ theme }) => theme.colors.textMuted};
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

const PrivacyNote = styled.p`
  margin: 0 0 14px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrap = styled.div`
  flex: 1 1 200px;
  min-width: 160px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 0;
  }
`;

const FilterSelect = styled(SelectDropdown)`
  flex: 1;
  min-width: 120px;
`;

const Presets = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgSecondary};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { display: flex; }
`;

const PresetBtn = styled.button<{ $active?: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 5px 11px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active, theme }) => ($active ? theme.colors.textPrimary : theme.colors.textMuted)};
  background: ${({ $active, theme }) => ($active ? theme.colors.bgPrimary : 'transparent')};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { flex: 1; }
`;

const Range = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { display: flex; flex-wrap: wrap; }
`;

const DateInput = styled.input`
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  color-scheme: ${({ theme }) => (theme.mode === 'dark' ? 'dark' : 'light')};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { flex: 1; min-width: 0; }
`;

/* 목록 컨테이너: 길어져도 앱 화면(뷰포트)을 넘지 않도록 높이를 가두고
 * 내부 세로 스크롤 + 페이지네이션으로 끊는다. dvh 로 모바일 주소창 변화 대응. */
const ListScroll = styled.div`
  max-height: 60vh;
  max-height: 60dvh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};

  scrollbar-width: thin;
`;

/* 모바일: 컬럼이 많은 표가 카드를 넘쳐 깨지지 않도록 가로 스크롤로 가둔다. */
const TableWrap = styled.div`
  width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 680px;
  }
`;

const Th = styled.th<{ $right?: boolean }>`
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  padding: 8px 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
  /* 세로 스크롤 시 헤더 고정 */
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const Td = styled.td<{ $right?: boolean }>`
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  padding: 8px 10px;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  vertical-align: middle;
`;

const UserCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const UserName = styled.span`
  font-weight: 600;
  white-space: nowrap;
`;

const UserEmail = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
`;

const FeatureChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`;

const TimeText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
`;

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  completed: { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
  failed: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444' },
  cancelled: { bg: 'rgba(128,128,128,0.14)', color: '#888' },
  in_progress: { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6' },
};

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;
  ${({ $status }) => {
    const c = STATUS_COLORS[$status] ?? STATUS_COLORS.in_progress;
    return css`background: ${c.bg}; color: ${c.color};`;
  }}
`;

const Tokens = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LinkBtn = styled.button`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.75rem;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.textMuted};
  &:hover { color: ${({ theme }) => theme.colors.textPrimary}; }
`;

const DetailCell = styled.td`
  padding: 0 10px 12px;
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const Conversation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 4px 4px;
`;

const Msg = styled.div<{ $role: string }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
`;

const MsgRole = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const MsgBubble = styled.div<{ $role: string }>`
  max-width: 86%;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  font-size: 0.8125rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ $role, theme }) =>
    $role === 'user' ? theme.colors.bgHover : theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const Pagination = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 14px;
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

const PageNavBtn = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    border-color: ${({ theme }) => theme.colors.borderInput};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const PageIndicator = styled.span`
  min-width: 72px;
  text-align: center;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
